import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FiCrosshair } from 'react-icons/fi';

const LAT_LNG_ZOOM = 16;
const LAT_LNG_PATTERN = /^\s*(-?\d+(?:\.\d+)?)\s*(?:,\s*|\s+)(-?\d+(?:\.\d+)?)\s*$/;
// One DMS coordinate, e.g. 31°27'46.7"N. Minutes/seconds optional; accepts typographic primes/quotes.
const DMS_PART = `(\\d+(?:\\.\\d+)?)\\s*°\\s*(?:(\\d+(?:\\.\\d+)?)\\s*['′’]\\s*)?(?:(\\d+(?:\\.\\d+)?)\\s*(?:"|″|”|'')\\s*)?([NSEW])`;
const DMS_PATTERN = new RegExp(`^\\s*${DMS_PART}\\s*,?\\s*${DMS_PART}\\s*$`, 'i');

const dmsToDecimal = (deg: string, min: string | undefined, sec: string | undefined, hemisphere: string) => {
  const value = parseFloat(deg) + (min ? parseFloat(min) / 60 : 0) + (sec ? parseFloat(sec) / 3600 : 0);
  return /[SW]/i.test(hemisphere) ? -value : value;
};

/**
 * Parse decimal degrees ("lat, lng" / "lat lng" / "lat,lng") or DMS
 * (31°27'46.7"N 100°26'30.6"W). Returns null on bad format.
 */
export const parseLatLng = (input: string): { lat: number; lng: number } | null => {
  const decimal = LAT_LNG_PATTERN.exec(input);
  if (decimal) {
    return { lat: parseFloat(decimal[1]), lng: parseFloat(decimal[2]) };
  }

  const dms = DMS_PATTERN.exec(input);
  if (!dms) {
    return null;
  }
  const first = { value: dmsToDecimal(dms[1], dms[2], dms[3], dms[4]), isLat: /[NS]/i.test(dms[4]) };
  const second = { value: dmsToDecimal(dms[5], dms[6], dms[7], dms[8]), isLat: /[NS]/i.test(dms[8]) };
  // Need exactly one N/S and one E/W; allow either order.
  if (first.isLat === second.isLat) {
    return null;
  }
  return first.isLat ? { lat: first.value, lng: second.value } : { lat: second.value, lng: first.value };
};

const ControlWrapper = styled.div`
  position: relative;
  margin: 10px;
  font-family: 'DM Sans', -apple-system, sans-serif;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 2px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: ${(p) => (p.$active ? '#10b981' : '#666')};
  box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
  cursor: pointer;
`;

const Popover = styled.form`
  position: absolute;
  top: 0;
  right: 48px;
  width: 260px;
  padding: 10px;
  border-radius: 6px;
  background: #0f172a;
  border: 1px solid rgba(52, 211, 153, 0.45);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  text-align: left;

  label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    color: #cbd5e1;
  }

  .row {
    display: flex;
    gap: 6px;
    width: 240px;
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 6px 8px;
    font-size: 13px;
    color: #f8fafc;
    background: #020617;
    border: 1px solid rgba(148, 163, 184, 0.45);
    border-radius: 4px;
    outline: none;
    margin-left: 14px
  }

  input:focus {
    border-color: #34d399;
  }

  button[type='submit'] {
    padding: 6px 12px;
    font-size: 13px;
    color: #020617;
    background: #34d399;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
  }

  .error {
    margin-top: 6px;
    font-size: 12px;
    color: #f87171;
  }

  .clear-pin {
    margin-top: 8px;
    padding: 0;
    font-size: 12px;
    color: #34d399;
    background: none;
    border: 0;
    cursor: pointer;
    text-decoration: underline;
  }
`;

interface ILatLngSearchControlProps {
  gMap: any;
  gMaps: any;
}

/**
 * Map control (RIGHT_TOP) that pans/zooms the map to an entered lat/long and drops a pin.
 * Rendered through a portal into a div registered with gMap.controls so it stacks with
 * the native and custom right-side controls.
 */
const LatLngSearchControl: React.FC<ILatLngSearchControlProps> = ({ gMap, gMaps }) => {
  const [container] = useState(() => document.createElement('div'));
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [hasPin, setHasPin] = useState(false);
  const markerRef = useRef<any>(null);

  useEffect(() => {
    if (!gMap || !gMaps) {
      return;
    }
    const controls = gMap.controls[gMaps.ControlPosition.RIGHT_TOP];
    controls.push(container);

    // Keep typing in the input from triggering the map's keyboard shortcuts.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
      e.stopPropagation();
    };
    container.addEventListener('keydown', onKeyDown);

    return () => {
      container.removeEventListener('keydown', onKeyDown);
      const index = controls.getArray().indexOf(container);
      if (index > -1) {
        controls.removeAt(index);
      }
    };
  }, [gMap, gMaps, container]);

  useEffect(
    () => () => {
      markerRef.current?.setMap(null);
    },
    []
  );

  const clearPin = () => {
    markerRef.current?.setMap(null);
    markerRef.current = null;
    setHasPin(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const coords = parseLatLng(value);
    if (!coords) {
      setError(`Use format: 29.7604, -95.3698 or 31°27'46.7"N 100°26'30.6"W`);
      return;
    }
    setError('');
    clearPin();
    markerRef.current = new gMaps.Marker({
      position: coords,
      map: gMap,
      title: `${coords.lat}, ${coords.lng}`,
    });
    setHasPin(true);
    gMap.panTo(coords);
    gMap.setZoom(LAT_LNG_ZOOM);
    setOpen(false);
  };

  if (!gMap || !gMaps) {
    return null;
  }

  return ReactDOM.createPortal(
    <ControlWrapper>
      <ToggleButton
        type="button"
        $active={open}
        title="Go to latitude / longitude"
        aria-label="Go to latitude / longitude"
        onClick={() => setOpen((prev) => !prev)}
      >
        <FiCrosshair size={22} />
      </ToggleButton>
      {open && (
        <Popover onSubmit={handleSubmit}>
          <label htmlFor="lat-lng-search-input">Go to coordinates</label>
          <div className="row">
            <input
              id="lat-lng-search-input"
              autoFocus
              autoComplete="off"
              placeholder="29.7604, -95.3698"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                if (error) {
                  setError('');
                }
              }}
            />
            <button type="submit">Go</button>
          </div>
          {error && <div className="error">{error}</div>}
          {hasPin && (
            <button type="button" className="clear-pin" onClick={clearPin}>
              Clear pin
            </button>
          )}
        </Popover>
      )}
    </ControlWrapper>,
    container
  );
};

export default LatLngSearchControl;
